import { useState, useId } from 'react';
import { Eye, EyeOff, AlertCircle, CheckCircle } from 'lucide-react';

interface FormFieldProps {
  label: string;
  type: 'text' | 'email' | 'password';
  value: string;
  onChange: (value: string) => void;
  error?: string;
  success?: string;
  required?: boolean;
  placeholder?: string;
  autoComplete?: string;
  disabled?: boolean;
  hint?: string;
  onFocus?: () => void;
}

export const FormField = ({
  label,
  type,
  value,
  onChange,
  error,
  success,
  required = false,
  placeholder,
  autoComplete,
  disabled = false,
  hint,
  onFocus,
}: FormFieldProps) => {
  const [showPassword, setShowPassword] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  const fieldId = useId();
  const hintId = useId();
  const errorId = useId();

  const inputType = type === 'password' && showPassword ? 'text' : type;
  const hasError = !!error;
  const hasSuccess = !!success && !hasError;

  return (
    <div className="space-y-2">
      {/* Label */}
      <label
        htmlFor={fieldId}
        className="block text-sm font-medium transition-colors duration-200"
        style={{ color: 'var(--color-text-primary)' }}
      >
        {label}
        {required && (
          <span className="ml-1 text-red-500" aria-label="required">
            *
          </span>
        )}
      </label>

      {/* Input Container */}
      <div className="relative">
        <input
          id={fieldId}
          type={inputType}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onFocus={() => {
            setIsFocused(true);
            onFocus?.();
          }}
          onBlur={() => setIsFocused(false)}
          placeholder={placeholder}
          autoComplete={autoComplete}
          disabled={disabled}
          required={required}
          aria-describedby={hint ? hintId : error ? errorId : undefined}
          aria-invalid={hasError}
          className={`
            w-full px-4 py-3 rounded-lg border transition-all duration-200
            focus:outline-hidden focus:ring-2 focus:ring-offset-2
            disabled:opacity-50 disabled:cursor-not-allowed
            ${
              hasError
                ? 'border-red-400 focus:border-red-500 focus:ring-red-500/20'
                : hasSuccess
                  ? 'border-green-400 focus:border-green-500 focus:ring-green-500/20'
                  : isFocused
                    ? 'focus:ring-teal-500/20'
                    : ''
            }
          `}
          style={{
            backgroundColor: 'var(--color-bg-secondary)',
            borderColor: hasError
              ? '#f87171'
              : hasSuccess
                ? '#4ade80'
                : isFocused
                  ? 'var(--color-accent-teal)'
                  : 'var(--color-border-default)',
            color: 'var(--color-text-primary)',
            boxShadow: isFocused
              ? `0 0 0 3px ${hasError ? 'rgba(239, 68, 68, 0.1)' : hasSuccess ? 'rgba(34, 197, 94, 0.1)' : 'rgba(20, 184, 166, 0.1)'}`
              : 'none',
          }}
        />

        {/* Password Toggle */}
        {type === 'password' && (
          <button
            type="button"
            onClick={() => setShowPassword(!showPassword)}
            className="absolute right-3 top-1/2 -translate-y-1/2 p-1 rounded-md transition-colors duration-200 hover:bg-black/5 dark:hover:bg-white/5 focus:outline-hidden focus:ring-2 focus:ring-teal-500/50"
            aria-label={showPassword ? 'Hide password' : 'Show password'}
          >
            {showPassword ? (
              <EyeOff className="w-5 h-5" style={{ color: 'var(--color-text-tertiary)' }} />
            ) : (
              <Eye className="w-5 h-5" style={{ color: 'var(--color-text-tertiary)' }} />
            )}
          </button>
        )}

        {/* Status Icons - only show when not a password field */}
        {type !== 'password' && (hasError || hasSuccess) && (
          <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none">
            {hasError ? (
              <AlertCircle className="w-5 h-5 text-red-500" />
            ) : (
              <CheckCircle className="w-5 h-5 text-green-500" />
            )}
          </div>
        )}
      </div>

      {/* Hint Text */}
      {hint && !error && !success && (
        <p
          id={hintId}
          className="text-sm transition-colors duration-200"
          style={{ color: 'var(--color-text-tertiary)' }}
        >
          {hint}
        </p>
      )}

      {/* Error Message */}
      {error && (
        <div
          id={errorId}
          className="flex items-center gap-2 text-sm text-red-600 animate-fade-in"
          role="alert"
          aria-live="polite"
        >
          <AlertCircle className="w-4 h-4 shrink-0" />
          <span>{error}</span>
        </div>
      )}

      {/* Success Message */}
      {success && !error && (
        <div
          className="flex items-center gap-2 text-sm text-green-600 animate-fade-in"
          role="status"
          aria-live="polite"
        >
          <CheckCircle className="w-4 h-4 shrink-0" />
          <span>{success}</span>
        </div>
      )}
    </div>
  );
};
